import { clsx } from '@appvantageasia/core-utils';
import { ChevronLeft } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import Logo from '@/components/blocks/Logo';
import ScrollArea from '@/components/blocks/ScrollArea';
import Button from '@/components/common/Button';
import useMediaQuery from '@/utils/useMediaQuery';
import type { NavigateItem } from '../types';
import FooterMenu from './FooterMenu';
import LogoutFooter from './LogoutFooter';
import MenuItem from './MenuItem';
import MenuOverlayPortal from './MenuOverlayPortal';
import SubMenuItem, { isPathnameMatch } from './SubMenuItem';

interface SidebarProps {
    menus: NavigateItem[];
    collapsed: boolean;
    setCollapsed: (value: boolean) => void;
    hasNoSubMenu: boolean;
    setHasNoSubMenu: (value: boolean) => void;
}
const Sidebar = ({ menus, collapsed, setCollapsed, hasNoSubMenu, setHasNoSubMenu }: SidebarProps) => {
    const isDesktop = useMediaQuery('(min-width: 1280px)');
    const { pathname } = useLocation();
    const navigate = useNavigate();

    // ============ State ============
    const [activeMenuIndex, setActiveMenuIndex] = useState<number | null>(null);
    const [isMobileOverlayOpen, setIsMobileOverlayOpen] = useState(false);
    const [isIconOnlyMode, setIsIconOnlyMode] = useState(false);

    // ============ Derived Data ============
    const activeMenu = activeMenuIndex !== null ? menus[activeMenuIndex] : null;
    const subMenuItems = activeMenu?.child ?? [];
    const subMenuTitle = activeMenu?.name ?? '';

    // Menu lists
    const mainMenuItems = useMemo(() => menus.filter(item => item.key !== 'settings'), [menus]);
    const settingsMenuIndex = useMemo(() => menus.findIndex(item => item.key === 'settings'), [menus]);

    // ============ UI Visibility States ============
    const shouldShowSubMenu = collapsed && !isDesktop && isIconOnlyMode && !hasNoSubMenu;
    const shouldHideIconMenu = !isDesktop && !collapsed && isIconOnlyMode;
    const shouldShowMobileOverlay = !isDesktop && (isMobileOverlayOpen || collapsed);
    const shouldShowSubMenuPanel = shouldShowSubMenu || (!collapsed && !isIconOnlyMode && !hasNoSubMenu);

    // ============ State Update Helpers ============
    const applySidebarStateForMenu = useCallback(
        (hasChildren: boolean) => {
            if (hasChildren) {
                setCollapsed(false);
                setIsIconOnlyMode(false);
                setHasNoSubMenu(false);
                if (!isDesktop) setIsMobileOverlayOpen(true);
            } else {
                setCollapsed(!isDesktop ? false : true);
                setIsIconOnlyMode(true);
                setHasNoSubMenu(true);
                if (!isDesktop) setIsMobileOverlayOpen(false);
            }
        },
        [isDesktop, setCollapsed, setHasNoSubMenu]
    );

    const closeMobileSidebar = useCallback(() => {
        setCollapsed(true);
        setIsIconOnlyMode(true);
        setIsMobileOverlayOpen(false);
        setHasNoSubMenu(true);
    }, [setCollapsed, setHasNoSubMenu]);

    const closeMobileOverlay = useCallback(() => {
        setIsMobileOverlayOpen(false);
        setIsIconOnlyMode(true);
        setCollapsed(false);
        setHasNoSubMenu(false);
    }, [setCollapsed, setHasNoSubMenu]);

    // ============ Event Handlers ============
    const onMenuClick = useCallback(
        (index: number) => {
            setActiveMenuIndex(index);
            const selectedMenu = menus[index];
            const hasChildren = !!(selectedMenu.child && selectedMenu.child.length > 0);

            // Execute navigation or action for menus without children
            if (!hasChildren) {
                if (selectedMenu.path) {
                    void navigate(selectedMenu.path);
                }

                if (selectedMenu.onClick) {
                    selectedMenu.onClick();
                }
            }

            // Update sidebar state based on menu type
            applySidebarStateForMenu(hasChildren);
        },
        [menus, navigate, applySidebarStateForMenu]
    );

    // Helper: Find menu index matching the pathname
    const findMatchingMenu = useCallback(
        (pathname: string) => {
            for (let index = 0; index < menus.length; index++) {
                const item = menus[index];

                // Direct path match (no children)
                if (item?.path && isPathnameMatch(item.path, pathname)) {
                    return { index, hasChildren: false };
                }

                // Child path match
                if (item?.child) {
                    for (const childItem of item.child) {
                        if (isPathnameMatch(childItem?.path || '', pathname)) {
                            return { index, hasChildren: true };
                        }
                    }
                }
            }
            return null;
        },
        [menus]
    );

    // ============ Sync with Route Changes ============
    // this has to stay an effect: part of the state (collapsed, hasNoSubMenu) is owned by the parent layout
    // and a parent state cannot be updated while this component renders
    useEffect(() => {
        const matchResult = findMatchingMenu(pathname);

        // Update state based on match result
        // oxlint-disable-next-line react/set-state-in-effect
        setActiveMenuIndex(matchResult?.index ?? null);
        setIsMobileOverlayOpen(false);

        if (matchResult) {
            // Menu matched
            setCollapsed(isDesktop ? !matchResult.hasChildren : false);
            setIsIconOnlyMode(!matchResult.hasChildren);
            setHasNoSubMenu(!matchResult.hasChildren);
        } else {
            // No match - reset to initial state
            setCollapsed(isDesktop);
            setIsIconOnlyMode(false);
            setHasNoSubMenu(true);
        }

        // On mobile, always set icon-only mode to true
        if (!isDesktop) {
            setIsIconOnlyMode(true);
        }
    }, [pathname, findMatchingMenu, isDesktop, setCollapsed, setHasNoSubMenu]);

    const onOpenSetting = useCallback(() => {
        if (menus.length > 0) {
            onMenuClick(menus.length - 1);
        }
    }, [menus, onMenuClick]);

    return (
        <>
            <div className="main-sidebar pointer-events-none fixed start-0 top-0 z-60 flex h-full xl:z-10 print:hidden">
                <div
                    className={clsx(
                        'border-default-200 dark:border-default-300 bg-card pointer-events-auto relative z-20 flex h-full w-[72px] flex-col border-r border-dashed transition-all duration-300',
                        {
                            'ltr:-translate-x-full ltr:xl:translate-x-0 rtl:translate-x-full rtl:xl:translate-x-0':
                                shouldHideIconMenu,
                            'translate-x-0': collapsed,
                        }
                    )}
                >
                    <div className="pt-4">
                        <Logo />
                    </div>
                    {/* end logo */}
                    <ScrollArea className="grow pt-6">
                        {mainMenuItems.map((item, i) => (
                            <MenuItem
                                activeIndex={activeMenuIndex}
                                key={item.key}
                                index={i}
                                onClick={onMenuClick}
                                item={item}
                            />
                        ))}
                    </ScrollArea>
                    <FooterMenu index={settingsMenuIndex} activeIndex={activeMenuIndex} onOpenSetting={onOpenSetting} />
                </div>
                {/* end small menu */}

                <div
                    className={clsx(
                        'border-default-200 bg-card pointer-events-auto relative z-10 flex h-full w-[228px] flex-col border-r transition-all duration-300',
                        {
                            'translate-x-[calc(-100%-72px)] rtl:translate-x-[calc(100%+72px)]': !shouldShowSubMenuPanel,
                        }
                    )}
                >
                    <h2 className="text-default-700 sticky top-0 z-50 flex items-center justify-between gap-4 bg-transparent px-4 py-4 text-lg font-semibold capitalize">
                        <span className="block">{subMenuTitle}</span>
                        {!isDesktop && (
                            <Button
                                size="icon"
                                variant="ghost"
                                onClick={closeMobileSidebar}
                                className="h-8 w-8 rounded-full"
                            >
                                <ChevronLeft className="h-5 w-5" />
                            </Button>
                        )}
                    </h2>
                    <ScrollArea className="h-[calc(100%-40px)] grow">
                        <div className="px-4" dir="ltr">
                            <ul>
                                {subMenuItems?.map(item => (
                                    <li key={item.key} className="mb-1.5 last:mb-0">
                                        <SubMenuItem item={item} />
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </ScrollArea>
                    <LogoutFooter />
                </div>
                {/* end main panel */}
            </div>
            {!isDesktop && <MenuOverlayPortal isOpen={shouldShowMobileOverlay} onClose={closeMobileOverlay} />}
        </>
    );
};

export default Sidebar;
